import qs from 'querystring'
import { isEmpty } from 'lodash'
import { errorTypes } from './error-types'
import { PRODUCT_NAMES } from './cfs-card'
import { getUserCountryCode } from './common'
import { MANAGEMENT_ACTION_TABS, FLOWS } from './ucs'

const { CONSUMER_DEBIT_CARD, CONSUMER_VIRTUAL_DEBIT_CARD, BUSINESS_DEBIT_CARD } = PRODUCT_NAMES
const { ENROLL_CARD, ACTIVATE_CARD } = FLOWS

export const PP_HOME = '/'
export const START_ENROLL = '/start/enroll'

/*=========================================
 * summarynodeweb (My Account Summary)
 *========================================*/

export const PP_SUMMARY = '/myaccount/summary'

/*=========================================
 * moneynodeweb (Wallet)
 *========================================*/

export const WALLET_BANK_DETAILS = '/myaccount/money/banks/@bankId'
export const WALLET_BANK_AUTHORIZATION_FLOW =
  '/myaccount/money/banks/@bankId/authorization?action=initiate-paypal-held-authorization-for-sepa'
export const WALLET_BANK_DETAILS_FLOW = '/myaccount/money/flow/banks/@bankId'

export const WALLET_CARD_DETAILS_FLOW = '/myaccount/money/flow/cards/@cardId'

/*=========================================
 * bankingbundlenodeweb (Old PPDC flows)
 *========================================*/

function getBankingBundleRoutePath(routePath = '') {
  return `/myaccount/bundle${routePath}`
}
export const BB_PPCC_HOME = getBankingBundleRoutePath('/cards/home')
export const BB_PPCC_LEARN_MORE = getBankingBundleRoutePath('/learnmore')
export const BB_PPCC_UPGRADE = getBankingBundleRoutePath('/upgrade')
export const BB_PPCC_STANDALONE_ENROLL = getBankingBundleRoutePath('/cards/enroll')
export const BB_PPCC_ADD_MONEY = getBankingBundleRoutePath('/addmoney')
export const BB_PPCC_STANDALONE_ENROLL_LEARN_MORE = getBankingBundleRoutePath(
  '/enroll/learnmore?intent=conscashcardonlyenrollment',
)
export const BB_PPCC_LOW_BALANCE = getBankingBundleRoutePath('/settings/lowbalance')
export const BB_PPCC_ACTIVATE = getBankingBundleRoutePath('/cards/activate')
export const BB_DIRECT_DEPOSIT = getBankingBundleRoutePath('/vba/get-started')
export const BALANCE_BUNDLE_FLOW_URL =
  '/myaccount/balance/get-started/balance-bundle?entry_point=GET_PAYPAL_CARD&flow_intent=STANDALONE'

// Add money / Transfer
export const ADD_MONEY = '/myaccount/money/balances/transfer'

export const getBankingBundleCardsHomeWithQueryParameters = (queryParameters = {}) => {
  const queryString = qs.stringify(queryParameters)
  if (queryString) {
    return BB_PPCC_HOME + '?' + queryString
  }
  return BB_PPCC_HOME
}

/*=========================================
 * debitcardnodeweb (BDMC)
 *========================================*/

function getDebitCardRoutePath(routePath = '') {
  return `/myaccount/debit-card${routePath}`
}

export const DC_BDMC_HOME = getDebitCardRoutePath()
export const DC_BDMC_APPLY = getDebitCardRoutePath('/apply')
export const DC_BDMC_SUMMARY = getDebitCardRoutePath('/v2/summary')
export const DC_UCS_CONFIRM_CARD_ADDRESSS = getDebitCardRoutePath('/v2/confirm-card-address')
export const DC_BDMC_EDIT_BILLING_ADDRESS = getDebitCardRoutePath(
  '/v2/edit-billing-address/@cardId',
)
export const DC_BDMC_ACTIVATE = getDebitCardRoutePath('/v2/activate/@cardId')
export const DC_BDMC_ENROLL_PRIMARY = getDebitCardRoutePath('/v2/enroll/primary')

/*======================================================
 * ucsnodeweb (Common card flows supporting PPDC, BDMC)
 *=====================================================*/

export const UCS_BASE_PATH = '/myaccount/debitcards'
export const CHANGE_ADDRESS_PATH = '/address/change'
export const STATIC_PAGE_BASE_PATH = '/page'

export const cardSummaryUrl = '/search'

export const getUcsRoutePath = (routePath = '') => {
  return `${UCS_BASE_PATH}${routePath}`
}

function formatManagementRouteUrl(
  path,
  {
    productName,
    queryParams,
    returnToManageTab = false,
    includeReturnUri = false,
    includeCancelUri = false,
  },
) {
  const cardManagementUrl = `${UCS_BASE_PATH}/summary`
  const uriQueryParameters = qs.stringify({
    productName,
    ...(returnToManageTab
      ? {
          selectedTab: MANAGEMENT_ACTION_TABS.MANAGE,
        }
      : {}),
  })

  const queryParameters = qs.stringify({
    ...queryParams,
    ...(includeReturnUri && { returnUri: `${cardManagementUrl}?${uriQueryParameters}` }),
    ...(includeCancelUri && { cancelUri: `${cardManagementUrl}?${uriQueryParameters}` }),
  })

  return `${UCS_BASE_PATH}${path}?${queryParameters}`
}

export const getManagementRouteUrl = (card, options = {}) => {
  const { cardId, productName = CONSUMER_DEBIT_CARD } = card
  const {
    includeReturnUri = true,
    includeCancelUri = true,
    returnToManageTab = true,
    queryParams = {},
  } = options

  const sharedOptions = {
    productName,
    queryParams,
    returnToManageTab,
  }

  return {
    viewPinUrl: formatManagementRouteUrl(`/manage/${cardId}/view-pin`, {
      ...sharedOptions,
      includeReturnUri,
    }),
    changePinUrl: formatManagementRouteUrl(`/manage/${cardId}/change-pin`, {
      ...sharedOptions,
      includeReturnUri,
    }),
    replaceCardUrl: formatManagementRouteUrl(`/manage/${cardId}/replace`, {
      ...sharedOptions,
      queryParams: {
        productName,
        ...queryParams,
      },
      includeCancelUri,
    }),
    requestPhysicalCardUrl: formatManagementRouteUrl(`/manage/${cardId}/request-physical-card`, {
      ...sharedOptions,
      includeCancelUri,
    }),
    cancelCardUrl: formatManagementRouteUrl(`/manage/${cardId}/cancel`, {
      ...sharedOptions,
      includeReturnUri,
    }),
    editFundingSourceUrl: formatManagementRouteUrl(`/manage/${cardId}/funding`, {
      ...sharedOptions,
      includeReturnUri,
    }),
    createPinURL: formatManagementRouteUrl(`/manage/${cardId}/create-pin`, {
      ...sharedOptions,
      includeReturnUri,
      queryParams: {
        productName,
        ...queryParams,
      },
    }),
  }
}

export const getSummaryUrlByProductName = (productName = CONSUMER_DEBIT_CARD) => {
  return getUcsRoutePath(`${cardSummaryUrl}?product_name=${productName}`)
}

export const UCS_VIRTUAL_CARD_SEARCH = getSummaryUrlByProductName(CONSUMER_VIRTUAL_DEBIT_CARD)
export const UCS_ACTIVATE = getUcsRoutePath('/activate')
export const UCS_ENROLL = getUcsRoutePath('/enroll')
export const UCS_MANAGEMENT = getUcsRoutePath('/summary')
export const UCS_REWARDS_HUB = getUcsRoutePath('/rewards')
export const UCS_LOW_BALANCE = getUcsRoutePath('/settings/lowbalance')
export const UCS_REPLACE = getUcsRoutePath('/manage/@cardId/replace')

export const getEnrollUrlByProductName = (req, productName = CONSUMER_DEBIT_CARD) => {
  const locationQuery = qs.stringify({
    ...req.query,
  })
  const queryString = locationQuery ? `?${locationQuery}` : `?productName=${productName}`
  return `${UCS_ENROLL}${queryString}`
}

export const getActivateUrlByProductName = (productName = CONSUMER_DEBIT_CARD) => {
  return `${UCS_ACTIVATE}?productName=${productName}`
}

/*======================================================
 * moneynodeweb (Wallet)
 *=====================================================*/

export function getWalletRoutePath(routePath = '') {
  return `/myaccount/money${routePath}`
}

export const WALLET_HOME = getWalletRoutePath()
export const WALLET_ADD_FUND = getWalletRoutePath('/balances/deposit')

/*======================================================
 * merchmoneynodeweb (Funds transfer)
 *=====================================================*/

export const FUNDS_TRANSFER_TO_BALANCE_PATH = '/fundstransfer/to?flowType=add'

/*======================================================
 * policydashboardnodeweb (CIP)
 *=====================================================*/

const DEBIT_CARD_CIP = '/policydashboard/process/compliance/debitcard'
const PROCESS_BASED_CIP = '/policydashboard/process/compliance/pid/@pid'
const UK_KYC = '/policydashboard/process/compliance/kyc'

export function generateCipRedirectUrl({
  flow = '',
  returnUrl = window.location.pathname + window.location.search,
  includeReturnUrl = true,
  additionalReturnUrlParams = {},
  additionalParams = {},
  pid = '',
}) {
  const PP_FLOW_MAP = {
    [ENROLL_CARD]: 'ppdc_enrollment_web',
    [ACTIVATE_CARD]: 'ppdc_activation_web',
  }

  if (!isEmpty(additionalReturnUrlParams)) {
    const queryStringWithoutQuestionMark = window.location.search.replace(/^\?/, '')
    returnUrl = `${window.location.pathname}?${qs.stringify({
      ...additionalReturnUrlParams,
      ...qs.parse(queryStringWithoutQuestionMark),
    })}`
  }

  const queryString = qs.stringify({
    useLiteUI: true,
    suppressDocUpload: false,
    enableSkip: true,
    attempt_intention: 'consumer_debit_card',
    source: 'ucsnodeweb',
    pp_flow: PP_FLOW_MAP[flow] || '',
    ...(includeReturnUrl && { returnUrl }),
    ...additionalParams,
  })

  const isProcessBased = !!pid
  if (isProcessBased) {
    return `${PROCESS_BASED_CIP.replace('@pid', pid)}?${queryString}`
  }
  return DEBIT_CARD_CIP + `?${queryString}`
}

export function generateKYCRedirectURL(selectedFundingInstrumentId){
  const currentURL = window.location.pathname + window.location.search
  const currentURLWithKYCRedirect = currentURL + `&KYC_Redirect=true&selectedFundingInstrumentId=${selectedFundingInstrumentId}`
  const queryString = qs.stringify({
    returnUrl: currentURLWithKYCRedirect,
  })
  return UK_KYC + `?${queryString}`
}

/*======================================================
 * Marketing  and Other PP Routes
 *=====================================================*/

export const BDMC_MARKETING_PAGE = '/business/financial-services/debit-card'
export const GET_PP_APP = '/digital-wallet'
export const BDMC_HOW_TO_ADD_MONEY =
  '/cshelp/article/how-do-i-add-money-to-paypal-for-my-business-debit-mastercard®-purchases-HELP619'

export function getPpAppDownloadPageUrl(req) {
  const countryCode = getUserCountryCode(req)
  return `/${countryCode}${GET_PP_APP}`
}

export function getUcsCardManagementPath(productName = CONSUMER_DEBIT_CARD, queryParameters = {}) {
  const queryString = qs.stringify(queryParameters)
  return `${UCS_MANAGEMENT}?productName=${productName}${queryString ? '&' + queryString : ''}`
}

export function getCardManagementPath(card = {}, queryParameters = {}) {
  const { productName } = card
  switch (productName) {
    case CONSUMER_DEBIT_CARD:
      return getUcsCardManagementPath(productName, queryParameters)
    case BUSINESS_DEBIT_CARD:
      return DC_BDMC_SUMMARY
    default:
      return PP_SUMMARY
  }
}

export const ACCOUNT_NOTIFICATION_PREFERENCES = '/myaccount/preferences/notifications'

/*=========================================
 * Mock Routes
 *========================================*/

export const MOCK_QUERY_INTERMITTENT_500_RESPONSE =
  errorTypes.INTERNAL_SERVER_ERROR + '_WITH_SUCCESS_ON_RETRY'
